import React, { useEffect, useState, useRef } from 'react';
import { 
  Box, 
  VStack, 
  Text, 
  HStack, 
  Image, 
  Flex, 
  Badge, 
  Icon, 
  Spinner, 
  Center, 
  Grid, 
  GridItem,
  Divider,
  useColorModeValue,
  Avatar
} from '@chakra-ui/react';
import { motion } from 'framer-motion';
import { 
  Award, 
  BookOpen, 
  Briefcase, 
  GraduationCap,
  Heart,
  Quote,
  Code,
  Database,
  Brain,
  Users,
  Compass,
  Workflow
} from 'lucide-react';
import { MethodistSlide } from '../../components/base/MethodistSlide';
import { getPresenterPhotoUrl } from '../../firebase/presenterPhoto';
import useLogoUrl from '../../hooks/useLogoUrl';
import { useAudio } from '../../context/AudioContext';

// Methodist color palette
const methodistBlue = '#0066CC';
const methodistNavy = '#002D72';

interface PresenterBioSlideProps {
  onNext?: () => void;
  onPrevious?: () => void;
  onOpenJIMIChat?: () => void;
  onHelp?: () => void;
  details?: {
    uiSample: 'resilience' | 'culture' | 'efficiency' | 'ai-companion' | 'crowdsourcing' | 'governance';
  };
  mode?: 'standalone' | 'deck';
  onStart?: () => void;
  onNavigateToSlide?: (slideId: string) => void;
  isSpeaking?: boolean;
  showCaptions?: boolean;
  captionsText?: string;
  isTransitioning?: boolean;
}

const presenter = {
  name: "Dr. Eleazar Flores"
};

const PresenterBioSlide: React.FC<PresenterBioSlideProps> = ({
  onNext,
  onPrevious,
  onOpenJIMIChat,
  onHelp,
  details,
  mode,
  onStart,
  onNavigateToSlide,
  isSpeaking,
  showCaptions,
  captionsText,
  isTransitioning
}) => {
  const [presenterPhoto, setPresenterPhoto] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  // Use the audio context
  const { playAudioForSlide, isPlaying, stopAllAudio } = useAudio();

  useEffect(() => {
    // Load presenter photo
    const loadPresenterPhoto = async () => {
      try {
        const photoUrl = await getPresenterPhotoUrl();
        setPresenterPhoto(photoUrl);
      } catch (error) {
        console.error('Error loading presenter photo:', error);
        // Use a fallback image if available
        setPresenterPhoto('/assets/images/presenter-photo.jpg');
      } finally {
        setIsLoading(false);
      }
    };

    loadPresenterPhoto();
    
    // Clean up function only - don't play audio here
    return () => {
      // No need to stop audio here as it will be handled by AudioContext
    };
  }, []);
  
  // Separate effect for audio playback
  useEffect(() => {
    // Only play audio if not in transition
    if (!isTransitioning) {
      const audioTimer = setTimeout(() => {
        console.log('Starting audio playback for presenter-bio slide...');
        playAudioForSlide('presenter-bio');
      }, 800);
      
      return () => {
        clearTimeout(audioTimer);
      };
    }
  }, [playAudioForSlide, isTransitioning]);
  
  return (
    <MethodistSlide title="Presenter Bio">
      <Box position="absolute" top="2" right="2" zIndex="10">
      </Box>
      <Box position="relative" h="100%" overflow="hidden">
        {/* Main content - no custom header */}
        <Grid 
          templateColumns={{ base: "1fr", md: "300px 1fr" }}
          gap={8}
          px={8}
          h="calc(100% - 20px)"
          overflow="hidden"
          mt={4}
        >
          {/* Left column - Photo and basic info */}
          <GridItem>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <VStack 
                spacing={4} 
                align="center"
              >
                <Box position="relative" mb={2}>
                  {isLoading ? (
                    <Center w="180px" h="180px">
                      <Spinner size="xl" color={methodistBlue} thickness="4px" />
                    </Center>
                  ) : !isLoading && presenterPhoto ? (
                    <Image
                      src={presenterPhoto}
                      alt="Dr. Eleazar Flores"
                      borderRadius="full"
                      boxSize="200px"
                      objectFit="cover"
                      border="3px solid"
                      borderColor="methodist.blue.300"
                      boxShadow="lg"
                    />
                  ) : (
                    // Fallback to Avatar with initials when all image options fail
                    <Avatar
                      size="2xl"
                      name="Eleazar Flores"
                      bg="methodist.blue.100"
                      color="methodist.blue.700"
                      border="3px solid"
                      borderColor="methodist.blue.300"
                      boxShadow="lg"
                    />
                  )}
                </Box>
                
                <VStack spacing={1}>
                  <Text fontSize="2xl" fontWeight="bold" color={methodistNavy}>
                    Dr. Eleazar Flores
                  </Text>
                  <Text fontSize="md" color={methodistBlue} fontWeight="medium">
                    Physician Well-Being Ambassador
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    MD, MBA, MSAA
                  </Text>
                </VStack>
                
                <Flex wrap="wrap" justify="center" gap={2} mt={2}>
                  <Badge colorScheme="blue" px={2} py={1} borderRadius="full">Family Medicine</Badge>
                  <Badge colorScheme="green" px={2} py={1} borderRadius="full">Health Innovation</Badge>
                  <Badge colorScheme="purple" px={2} py={1} borderRadius="full">AI in Healthcare</Badge>
                </Flex>
                
                <Box 
                  bg="gray.50" 
                  p={4} 
                  borderRadius="md" 
                  borderLeft="3px solid" 
                  borderColor={methodistBlue}
                  mt={4}
                  width="100%"
                >
                  <Flex alignItems="flex-start">
                    <Icon as={Quote} color={methodistBlue} boxSize={5} mr={2} mt={0.5} />
                    <Text fontSize="sm" fontStyle="italic" color="gray.600">
                      "As a physician with a passion for health innovation and AI, I believe the JIMI Platform represents our commitment to supporting clinician well-being through technology."
                    </Text>
                  </Flex>
                </Box>
              </VStack>
            </motion.div>
          </GridItem>

          {/* Right column - Professional details */}
          <GridItem overflowY="auto" pr={4}>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
            >
              <Grid templateColumns={{ base: "1fr", lg: "repeat(2, 1fr)" }} gap={6}>
                <GridItem>
                  <Box 
                    bg="white" 
                    p={4} 
                    borderRadius="md" 
                    boxShadow="sm" 
                    borderTop="3px solid" 
                    borderColor={methodistBlue}
                    h="100%"
                  >
                    <HStack mb={3}>
                      <Flex 
                        bg={methodistBlue} 
                        p={2} 
                        borderRadius="md" 
                        color="white"
                      >
                        <Briefcase size={18} />
                      </Flex>
                      <Text fontWeight="bold" fontSize="md">Professional Background</Text>
                    </HStack>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm">Medical Doctor (MD), Family Medicine, Board Certified</Text>
                      <Text fontSize="sm">Physician at Houston Methodist for over 10 years</Text>
                      <Text fontSize="sm">Physician Well-Being Ambassador</Text>
                      <Text fontSize="sm">Business Analyst</Text>
                      <Text fontSize="sm">Physician Technologist, Clinical Innovation</Text>
                    </VStack>
                  </Box>
                </GridItem>
                
                <GridItem>
                  <Box 
                    bg="white" 
                    p={4} 
                    borderRadius="md" 
                    boxShadow="sm" 
                    borderTop="3px solid" 
                    borderColor={methodistBlue}
                    h="100%"
                  >
                    <HStack mb={3}>
                      <Flex 
                        bg={methodistBlue} 
                        p={2} 
                        borderRadius="md" 
                        color="white"
                      >
                        <Code size={18} />
                      </Flex>
                      <Text fontWeight="bold" fontSize="md">Technical Expertise</Text>
                    </HStack>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm">Epic Physician Builder</Text>
                      <Text fontSize="sm">Data Scientist for HM Epic Cosmos</Text>
                      <Text fontSize="sm">AI Programmer</Text>
                      <Text fontSize="sm">Developer in Next.js, Python, SQL</Text>
                      <Text fontSize="sm">Clinical Innovation Technology Lead</Text>
                    </VStack>
                  </Box>
                </GridItem>
                
                <GridItem>
                  <Box 
                    bg="white" 
                    p={4} 
                    borderRadius="md" 
                    boxShadow="sm" 
                    borderTop="3px solid" 
                    borderColor={methodistBlue}
                    h="100%"
                  >
                    <HStack mb={3}>
                      <Flex 
                        bg={methodistBlue} 
                        p={2} 
                        borderRadius="md" 
                        color="white"
                      >
                        <GraduationCap size={18} />
                      </Flex>
                      <Text fontWeight="bold" fontSize="md">Education</Text>
                    </HStack>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm">MD, Houston Methodist Family Medicine Residency Program</Text>
                      <Text fontSize="sm">MBA, Rice University, Houston, TX</Text>
                      <Text fontSize="sm">MS in Applied Analytics, Columbia University</Text>
                    </VStack>
                  </Box>
                </GridItem>
                
                <GridItem>
                  <Box 
                    bg="white" 
                    p={4} 
                    borderRadius="md" 
                    boxShadow="sm" 
                    borderTop="3px solid" 
                    borderColor={methodistBlue}
                    h="100%"
                  >
                    <HStack mb={3}>
                      <Flex 
                        bg={methodistBlue} 
                        p={2} 
                        borderRadius="md" 
                        color="white"
                      >
                        <Database size={18} />
                      </Flex>
                      <Text fontWeight="bold" fontSize="md">Research & Innovation</Text>
                    </HStack>
                    <VStack align="start" spacing={2}>
                      <Text fontSize="sm">Health Innovation Specialist with focus on AI</Text>
                      <Text fontSize="sm">AI in Healthcare Research Contributor</Text>
                      <Text fontSize="sm">Stanford Model of Professional Fulfillment Advocate</Text>
                    </VStack>
                  </Box>
                </GridItem>
              </Grid>
              
              {/* Mission & Vision for JIMI Wellness Platform */}
              <Box 
                w="100%" 
                mt={6} 
                p={5} 
                bg="white" 
                borderRadius="md"
                boxShadow="sm"
                borderLeft="5px solid"
                borderColor={methodistNavy}
              >
                <HStack mb={3}>
                  <Flex 
                    bg={methodistNavy} 
                    p={2} 
                    borderRadius="md" 
                    color="white"
                  >
                    <Compass size={18} />
                  </Flex>
                  <Text fontWeight="bold" fontSize="md" color={methodistNavy}>
                    Mission & Vision for JIMI Wellness Digital Platform
                  </Text>
                </HStack>
                
                <Box mt={2}>
                  <Text fontSize="sm" mb={3} lineHeight="1.6">
                    My mission for the JIMI Wellness Platform is to create sustainable solutions that address burnout at its root causes rather than just treating symptoms. This platform represents a comprehensive approach to clinician well-being that combines technology with human-centered design.
                  </Text>
                  
                  <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap={4} mt={4}>
                    <GridItem>
                      <Flex 
                        direction="column" 
                        bg="gray.50" 
                        p={3} 
                        borderRadius="md" 
                        h="100%"
                        borderTop="2px solid"
                        borderColor="blue.400"
                      >
                        <Flex align="center" mb={2}>
                          <Icon as={Brain} color="blue.400" mr={2} />
                          <Text fontWeight="bold" fontSize="sm">AI-Driven Personalization</Text>
                        </Flex>
                        <Text fontSize="xs">Leveraging artificial intelligence to create tailored well-being experiences that adapt to each clinician's unique needs and preferences</Text>
                      </Flex>
                    </GridItem>
                    
                    <GridItem>
                      <Flex 
                        direction="column" 
                        bg="gray.50" 
                        p={3} 
                        borderRadius="md" 
                        h="100%"
                        borderTop="2px solid"
                        borderColor="orange.400"
                      >
                        <Flex align="center" mb={2}>
                          <Icon as={Users} color="orange.400" mr={2} />
                          <Text fontWeight="bold" fontSize="sm">Community Engagement</Text>
                        </Flex>
                        <Text fontSize="xs">Building a supportive community where clinicians can connect, share experiences, and collaborate on wellness initiatives</Text>
                      </Flex>
                    </GridItem>
                    
                    <GridItem>
                      <Flex 
                        direction="column" 
                        bg="gray.50" 
                        p={3} 
                        borderRadius="md" 
                        h="100%"
                        borderTop="2px solid"
                        borderColor="green.400"
                      >
                        <Flex align="center" mb={2}>
                          <Icon as={Workflow} color="green.400" mr={2} />
                          <Text fontWeight="bold" fontSize="sm">Workflow Optimization</Text>
                        </Flex>
                        <Text fontSize="xs">Streamlining clinical workflows to reduce administrative burden and help clinicians rediscover the joy in medicine</Text>
                      </Flex>
                    </GridItem>
                  </Grid>
                  
                  <Text fontSize="sm" mt={4} fontStyle="italic" color="gray.700">
                    By combining these elements, we can transform healthcare culture and support clinicians in rediscovering the joy in medicine while building a more sustainable healthcare system for the future.
                  </Text>
                </Box>
              </Box>
            </motion.div>
          </GridItem>
        </Grid>
      </Box>
    </MethodistSlide>
  );
};

export default PresenterBioSlide;
